import React, { useEffect, useState, useRef } from "react";
import AgGridLoader from "../AgGridLoader";
import { getHeaderClass } from "../../utils/agGridHelpers";
import { useSelector, useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { fetchPayPurpose } from "../../redux/paypurposeSlice";
import { fetchDonation } from "../../redux/donationSlice";
import { Button } from "@mui/material";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DonationFormModal from "../Donation/DonationFormModal";
import ConfirmationDialog from "../ConfirmationDialog";
import { clearAllFilters } from "../../utils/agGridHelpers";
import { logger } from "../../utils/logger";
import { getSystemFormattedTimestamp } from "../../utils/dateFormatted";
import SnackbarAlert from "../SnackBarAlert";
import { getPaypurposeColumns } from "./PaypurposeColumns";
import {
  handleRowDoubleclick,
  handleEditClick,
  handleDeleteClick,
  handleConfirmDelete,
  handleSubmit,
  handleModalSubmit,
  handleCloseFeedback,
} from "./utils/paypurposeHandlers";

const PaypurposeList = ({ onMenuSelect }) => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const formRef = useRef();
  const { role } = useSelector((state) => state.auth);
  const paypurpose = useSelector((state) => state.paypurpose?.paypurpose || []);
  const allDonations = useSelector((state) => state.donation?.donations || []);
  const loading = useSelector((state) => state.donation?.loading);
  const [editData, setEditData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPayPurposeId, setSelectedPayPurposeId] = useState(null);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const handleFetch = async () => {
      try {
        await dispatch(fetchDonation());
      } catch (err) {
        logger.error("Failed to fetch Donations:", err);
      }
    };
    handleFetch();
  }, []);

  useEffect(() => {
    const handleFetch = async () => {
      try {
        await dispatch(fetchPayPurpose());
      } catch (err) {
        logger.error("Failed to fetch PayPurpose:", err);
      }
    };
    handleFetch();
  }, [dispatch]);

  const columnDefs = getPaypurposeColumns(
    role,
    (params) => handleEditClick(params, allDonations, setEditData, setOpen),
    (params) =>
      handleDeleteClick(params, setSelectedPayPurposeId, setConfirmOpen)
  );

  const [gridReady, setGridReady] = useState(false);

  const onGridReady = (params) => {
    gridRef.current = params.api;
    setGridReady(true);
  };

  useEffect(() => {
    if (!gridReady || !gridRef.current) return;
    gridRef.current.setGridOption("loading", loading);
  }, [loading, gridReady]);

  const exportToExcel = () => {
    if (!gridRef.current) return;
    gridRef.current.exportDataAsCsv({
      fileName: `NpfUafDetails_export ${getSystemFormattedTimestamp()}.csv`,
    });
  };

  const handleClearAll = () => {
    if (!gridRef.current) {
      logger.warn("Grid not ready yet");
      return;
    }
    gridRef.current.setFilterModel(null);
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 200px)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          marginBottom: "10px",
        }}
      >
        <Button
          variant="contained"
          color="error"
          onClick={handleClearAll}
          sx={{ mr: 2 }}
        >
          Clear All Filters
        </Button>
        {role === "superadmin" || role === "admin1" || role === "admin2" ? (
          <Button onClick={exportToExcel} variant="contained" color="primary">
            <DriveFileMoveIcon /> Export
          </Button>
        ) : null}
      </div>
      <div
        className="ag-theme-alpine"
        style={{ height: "100%", width: "100%", overflow: "auto" }}
      >
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          defaultColDef={{ headerClass: getHeaderClass }}
          loadingOverlayComponent={AgGridLoader}
          loadingOverlayComponentParams={{ loadingMessage: "Loading records, please wait..." }}
          rowData={paypurpose
            .filter(
              (item) =>
                item.payPurpose === "NithiyaPoojaFund" ||
                item.payPurpose === "UtsavAnnadhanaFund"
            )
            .map((item) => ({
              ...item,
              poojaSortDate: item.poojaDate ? new Date(item.poojaDate) : null,
              poojaDate: item.poojaDate
                ? dayjs(item.poojaDate).format("DD/MMM")
                : "",
            }))}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          onRowDoubleClicked={(params) =>
            handleRowDoubleclick(params, onMenuSelect)
          }
          onFilterChanged={(params) => params.api.refreshHeader()}
          suppressMenuHide={true}
        />
      </div>

      <DonationFormModal
        open={open}
        onClose={() => setOpen(false)}
        editData={editData}
        onSubmit={(formData) =>
          handleSubmit(formData, dispatch, setFeedback, setOpen, setEditData)
        }
        formRef={formRef}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Delete PayPurpose"
        message="Are you sure you want to delete this PayPurpose record?"
        onConfirm={() =>
          handleConfirmDelete(
            selectedPayPurposeId,
            dispatch,
            setFeedback,
            setConfirmOpen,
            setSelectedPayPurposeId
          )
        }
        onCancel={() => setConfirmOpen(false)}
      />

      <SnackbarAlert
        feedback={feedback}
        onClose={() => handleCloseFeedback(feedback, setFeedback)}
      />
    </div>
  );
};

export default PaypurposeList;
